<template>
  <div class="flex justify-center">
    <InvoiceActionsMenu
      :invoice="invoice"
      :tippy-options="tippyOptions"
      :visible-actions="visibleActions"
      @invoice-action="handleInvoiceAction"
    >
      <template #trigger>
        <slot name="trigger">
          <SeraButton
            v-if="hasButtonMenu"
            text="Invoice Actions"
            icon="fa-regular fa-chevron-down"
            icon-position="right"
            data-cy="invoice-actions"
          />
        </slot>
      </template>
      <template #footer>
        <div
          v-if="allowToggleDisplayMembershipSavings"
          class="border-t-[1px] border-gray-300"
        >
          <div
            class="flex justify-between align-center whitespace-nowrap px-5 py-4"
          >
            <span class="!text-gray-700 font-medium text-[12px]">
              Show Membership Savings
            </span>
            <ToggleSwitch
              v-model="displayMembershipSavings"
              data-cy="display-membership-savings-toggle"
              @update:modelValue="handleToggleMembershipSavings"
            />
          </div>
        </div>
      </template>
    </InvoiceActionsMenu>
    <AcceptPayment
      v-if="paymentModal"
      :invoice="invoice"
      :disabled="invoice.financials.balance_cents <= 0"
      @hide-modal="paymentModal = false"
    />
    <WorkCompleteModal
      v-if="signatureModal"
      :invoice="invoice"
      @hide-modal="signatureModal = false"
    />
    <RefundModal
      v-if="refundModal"
      :invoice="invoice"
      @hide-modal="refundModal = false"
    />
    <ActionConfirmationModal
      v-if="resubmitModal"
      :modal-contents="resubmitModalContents"
      @hide-modal="resubmitModal = false"
    />
    <EditInvoiceDateModal
      v-if="editDateModal"
      :invoice="invoice"
      @hide-modal="editDateModal = false"
    />
    <EmailQuoteOrInvoiceModal
      v-if="showEmailInvoiceModal"
      :invoice="invoice"
      @hide-modal="showEmailInvoiceModal = false"
    />
    <VoidInvoiceModal
      v-if="voidModal"
      :invoice="invoice"
      @hide-modal="voidModal = false"
    />
    <UnapproveInvoiceModal
      v-if="unapproveModal"
      :invoice="invoice"
      @hide-modal="unapproveModal = false"
    />
    <InvoiceDetailsDrawer
      v-if="showInvoiceDetailsDrawer"
      :invoice="invoice"
      @hide-drawer="showInvoiceDetailsDrawer = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useInvoiceActions } from "@/lib/useInvoiceActions";
import { useJobWarnings } from "@/lib/useJobWarnings";

import SeraButton from "@/components/Button.vue";
import InvoiceActionsMenu from "@/components/Invoice/InvoiceActionsMenu.vue";
import InvoiceDetailsDrawer from "@/components/Invoice/InvoiceDetailsDrawer.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import AcceptPayment from "@/AcceptPayment/AcceptPayment.vue";
import EmailQuoteOrInvoiceModal from "@/EmailQuoteOrInvoice/EmailQuoteOrInvoiceModal.vue";
import EditInvoiceDateModal from "@/Invoice/EditInvoiceDateModal.vue";
import RefundModal from "@/Invoice/RefundModal.vue";
import UnapproveInvoiceModal from "@/Invoice/UnapproveInvoiceModal.vue";
import VoidInvoiceModal from "@/Invoice/VoidInvoiceModal.vue";
import WorkCompleteModal from "@/Invoice/WorkCompleteModal.vue";
import ActionConfirmationModal from "@/Reports/ActionConfirmationModal.vue";
import type { Invoice } from "@/typings/invoice";
import type { TippyOptions } from "@/typings/ui";
import { InvoiceAction } from "@/typings/ui";

const props = withDefaults(
  defineProps<{
    invoice: Invoice;
    tippyOptions?: TippyOptions;
    visibleActions?: InvoiceAction[];
    hasButtonMenu?: boolean;
    hideMembershipSavingsToggle?: boolean;
  }>(),
  {
    tippyOptions: () => ({}),
    visibleActions: undefined,
    hasButtonMenu: false,
    hideMembershipSavingsToggle: false
  }
);

const store = useStore();
const invoiceActions = useInvoiceActions();
const { withJobWarnings } = useJobWarnings();

const paymentModal = ref(false);
const signatureModal = ref(false);
const refundModal = ref(false);
const resubmitModal = ref(false);
const editDateModal = ref(false);
const showEmailInvoiceModal = ref(false);
const unapproveModal = ref(false);
const voidModal = ref(false);
const showInvoiceDetailsDrawer = ref(false);
const displayMembershipSavings = ref(props.invoice.display_membership_savings);

const tenant = computed(() => store.state.tenant);

const allowToggleDisplayMembershipSavings = computed(() => {
  return (
    store.getters.featureEnabledForTenant(
      "toggle_display_membership_savings"
    ) &&
    tenant.value?.display_membership_savings_ad_hoc &&
    !props.hideMembershipSavingsToggle &&
    !props.invoice.finalized &&
    props.invoice.links?.update_display_membership_savings
  );
});

const resubmitModalContents = computed(() => {
  if (!props.invoice) return {};
  return {
    message:
      "Resubmitting this invoice for billing will attempt to collect payment with the payment method on file.",
    title: "Confirm Rebilling",
    success_message: `Invoice #${props.invoice.invoice_id} submitted for rebilling.  Please check the status in a few minutes.`,
    event_name: "invoice-resubmitted",
    action_url: props.invoice.links?.resubmit,
    hide_input: true
  };
});

async function handleApproveInvoice() {
  const hasFulfilledAllItems = !props.invoice.items_attributes.find(
    (item) => item.equipment_request_status === "unfulfilled"
  );
  if (hasFulfilledAllItems) {
    signatureModal.value = true;
    return;
  }
  await store.dispatch("confirmChanges", {
    title: "Equipment Requested",
    message:
      "There are items that require equipment to be captured.<br/> Are you sure you wish to continue?"
  });
  signatureModal.value = true;
}

async function handleInvoiceAction({
  action
}: {
  id: number;
  action: InvoiceAction;
}) {
  switch (action) {
    case InvoiceAction.Approve:
      await handleApproveInvoice();
      break;
    case InvoiceAction.AcceptPayment:
      paymentModal.value = true;
      break;
    case InvoiceAction.IssueRefund:
      refundModal.value = true;
      break;
    case InvoiceAction.EditDate:
      withJobWarnings({
        action: () => {
          editDateModal.value = true;
        },
        record: props.invoice,
        context: "edit-invoice"
      });
      break;
    case InvoiceAction.Unapprove:
      withJobWarnings({
        action: () => {
          unapproveModal.value = true;
        },
        record: props.invoice,
        context: "edit-invoice"
      });
      break;
    case InvoiceAction.Void:
      voidModal.value = true;
      break;
    case InvoiceAction.Resubmit:
      resubmitModal.value = true;
      break;
    case InvoiceAction.Email:
      showEmailInvoiceModal.value = true;
      break;
    case InvoiceAction.EditDetails:
      showInvoiceDetailsDrawer.value = true;
      break;
    default:
      await invoiceActions.perform(props.invoice, action);
      break;
  }
}

function handleToggleMembershipSavings() {
  store
    .dispatch("updateInvoiceDisplayMembershipSavings", {
      url: props.invoice.links?.update_display_membership_savings,
      display_membership_savings: displayMembershipSavings.value
    })
    .then(() => {
      window.notify.success(
        "Successfully updated membership savings configuration."
      );
    });
}
</script>
